// src/components/UnresolvedImportItem.tsx
// インポート時にISBNで自動解決できなかった1冊について、タイトルで検索して
// ユーザーに正しい本を選んでもらうためのカード。
//
// 選択の流れ（誤クリック防止のため2段階にしている）:
//   1. 候補を1回クリック -> 「選択候補」としてテキスト表示するだけ（まだ確定しない）
//   2. 同じ候補をもう一度クリック -> 選択を確定する
//   3. 確定済みの候補をクリック -> 選択解除（やり直せる）

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { buildPrefixSearchUrl } from '../config/api';
import { generateCoverUrlSmart } from '../utils/generateCoverUrl';
import { PrefixSearchBook } from '../types/rlTypes';
import { ImportFailedItem } from '../utils/importApi';
import { useAppTokens } from '../context/ThemeModeContext';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

/**
 * ブクログのタイトルは「タイトル―サブタイトル (発売年)」のような癖の強い形式が多く、
 * また「暗夜行路 (新潮文庫)」のように出版社・レーベル名がカッコで入るケースもあるため、
 * ダッシュで区切った後のテキストをさらに「（」「(」で区切り、その手前だけを使う。
 * ReadLinkerのプレフィックス検索はタイトルの先頭一致でしか引っかからないため、
 * ダッシュ以降・カッコ以降・末尾の年号カッコを取り除いた「先頭部分」を検索クエリの初期値にする。
 */
export function deriveSearchQuery(title: string): string {
  const withoutYear = title.replace(/[\(（]\s*\d{4}年?\s*[\)）]\s*$/, '').trim();
  const beforeDash = withoutYear.split(/[－―\-]/)[0].trim();
  const beforeParen = beforeDash.split(/[（(]/)[0].trim();
  return beforeParen || beforeDash || withoutYear || title;
}

interface Props {
  item: ImportFailedItem;
  onSelectionChange: (rlBookId: number | null) => void;
}

const UnresolvedImportItem: React.FC<Props> = ({ item, onSelectionChange }) => {
  const TOKENS = useAppTokens();
  const [query, setQuery] = useState(() => deriveSearchQuery(item.title));
  const [results, setResults] = useState<PrefixSearchBook[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [previewId, setPreviewId] = useState<number | null>(null);
  const [confirmedId, setConfirmedId] = useState<number | null>(null);
  const [queryEdited, setQueryEdited] = useState(false);

  const runSearch = async (q: string) => {
    const trimmed = q.trim();
    if (!trimmed) return;
    setLoading(true);
    try {
      const res = await axios.get(buildPrefixSearchUrl(trimmed, 20, 0));
      setResults(res.data?.books ?? []);
    } catch (e) {
      setResults([]);
    } finally {
      setLoading(false);
      setSearched(true);
    }
  };

  // 初回自動検索
  useEffect(() => {
    runSearch(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResultClick = (book: PrefixSearchBook) => {
    if (confirmedId === book.rl_book_id) {
      // 確定済みをもう一度クリック -> 選択解除
      setConfirmedId(null);
      setPreviewId(null);
      onSelectionChange(null);
    } else if (previewId === book.rl_book_id) {
      // 候補を2回目クリック -> 確定
      setConfirmedId(book.rl_book_id);
      onSelectionChange(book.rl_book_id);
    } else {
      // 1回目クリック -> 候補として表示するだけ
      setPreviewId(book.rl_book_id);
    }
  };

  const previewBook = results.find((b) => b.rl_book_id === previewId);
  const confirmedBook = results.find((b) => b.rl_book_id === confirmedId);

  return (
    <Box
      sx={{
        background: TOKENS.cardBg,
        border: `1px solid ${confirmedId != null ? '#2e7d32' : TOKENS.border}`,
        borderRadius: '12px',
        p: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 12.5, color: TOKENS.text }}>{item.title || '(タイトル不明)'}</Typography>
          <Typography sx={{ fontSize: 11, color: TOKENS.textSub }}>
            {[item.author, item.publisher].filter(Boolean).join(' / ')}
          </Typography>
          {!queryEdited && (
            <Typography sx={{ fontSize: 10.5, color: TOKENS.textSub, mt: 0.25 }}>{item.reason}</Typography>
          )}
        </Box>
        {confirmedId != null && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <CheckCircleIcon sx={{ fontSize: 16, color: '#2e7d32' }} />
            <Typography sx={{ fontSize: 11, color: '#2e7d32', fontWeight: 700 }}>選択済み</Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <TextField
          size="small"
          fullWidth
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setQueryEdited(true);
          }}
          onKeyDown={(e) => e.key === 'Enter' && runSearch(query)}
          sx={{
            '& .MuiOutlinedInput-root': { color: TOKENS.text, background: TOKENS.buttonBg, borderRadius: '10px', fontSize: 12.5 },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.borderSoft },
          }}
        />
        <Button
          onClick={() => runSearch(query)}
          disabled={loading || !query.trim()}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontSize: 12,
            color: TOKENS.text,
            border: `1px solid ${TOKENS.borderSoft}`,
            flexShrink: 0,
          }}
        >
          {loading ? <CircularProgress size={14} sx={{ color: TOKENS.textSub }} /> : '検索'}
        </Button>
      </Box>

      {confirmedBook ? (
        <Typography sx={{ fontSize: 12, color: '#2e7d32', mb: 1 }}>
          ✓ 選択しました: {confirmedBook.title}
          {confirmedBook.responsibility ? ` / ${confirmedBook.responsibility}` : ''}
        </Typography>
      ) : previewBook ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 12, color: TOKENS.text }}>
            選択候補: {previewBook.title}
            {previewBook.responsibility ? ` / ${previewBook.responsibility}` : ''}
            {' '}— もう一度クリックすると選択を確定します
          </Typography>
          <Button
            component="a"
            href={`/book/${previewBook.rl_book_id}`}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
            sx={{
              borderRadius: '999px',
              textTransform: 'none',
              fontSize: 11,
              color: TOKENS.text,
              border: `1px solid ${TOKENS.borderSoft}`,
              px: 1.25,
              py: 0.25,
              minWidth: 0,
              flexShrink: 0,
            }}
          >
            書誌詳細を開く
          </Button>
        </Box>
      ) : null}

      {searched && !loading && results.length === 0 && (
        <Typography sx={{ fontSize: 11.5, color: TOKENS.textSub }}>検索結果が0件です</Typography>
      )}

      {results.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {results.map((book) => {
            const isConfirmed = confirmedId === book.rl_book_id;
            const isPreview = previewId === book.rl_book_id && !isConfirmed;
            return (
              <Box
                key={book.rl_book_id}
                onClick={() => handleResultClick(book)}
                sx={{
                  width: 80,
                  cursor: 'pointer',
                  border: `2px solid ${isConfirmed ? '#2e7d32' : isPreview ? TOKENS.accent : 'transparent'}`,
                  borderRadius: '8px',
                  p: 0.5,
                  textAlign: 'center',
                }}
              >
                <Box
                  component="img"
                  src={generateCoverUrlSmart(book.rl_book_id, book.title, book.isbn13)}
                  alt={book.title}
                  sx={{ width: '100%', height: 90, objectFit: 'contain', background: TOKENS.buttonBg, borderRadius: '4px' }}
                />
                <Typography
                  sx={{
                    fontSize: 10.5,
                    color: TOKENS.text,
                    mt: 0.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {book.title}
                </Typography>
                <Typography sx={{ fontSize: 9.5, color: TOKENS.textSub }} noWrap>
                  {book.publishing_date}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default UnresolvedImportItem;
